import { clearGupVisit } from '@/lib/gup';
import { JwtPayloadType } from '@/api/backend/types';
import { jwtDecode } from 'jwt-decode';
import { User as OidcUser, UserManager, WebStorageStateStore } from 'oidc-client-ts';

type TokenResponse = {
  access_token: string;
  id_token: string;
  refresh_token: string;
  session_state: string;
};

type SigninReturnState = {
  returnTo: string;
};

export class AuthService {
  static CLIENT_ID_KEY = 'auth_client_id' as const;

  private _clientId: string;
  private _oidcClient: UserManager;

  get oidcClient(): UserManager {
    return this._oidcClient;
  }

  get clientId(): string {
    return this._clientId;
  }

  constructor(clientId: string) {
    localStorage.setItem(AuthService.CLIENT_ID_KEY, clientId);

    this._clientId = clientId;
    this._oidcClient = new UserManager({
      authority: `${import.meta.env.VITE_KEYCLOAK_URL}/realms/${import.meta.env.VITE_KEYCLOAK_REALM}`,
      redirect_uri: window.location.origin,
      client_id: clientId,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      post_logout_redirect_uri: `${window.location.origin}/login`,
    });
  }

  static getOidcUserFromLocalStorage(): OidcUser | null {
    const clientId = localStorage.getItem(AuthService.CLIENT_ID_KEY);
    const oidcStorageString = localStorage.getItem(
      `oidc.user:${import.meta.env.VITE_KEYCLOAK_URL}/realms/${import.meta.env.VITE_KEYCLOAK_REALM}:${clientId}`,
    );

    return oidcStorageString ? OidcUser.fromStorageString(oidcStorageString) : null;
  }

  static isTokenExpired(): boolean {
    const user = AuthService.getOidcUserFromLocalStorage();

    if (!user || !user.access_token) {
      return true;
    }

    return user.expired ?? true;
  }

  static handleTokenExpired(): void {
    AuthService.clearLocalStorage();
  }

  /**
   * Renews an expired access token with the stored refresh token. The access token can expire while the user
   * is on another site (e.g. THEQA digital signing) even though the Keycloak session is still alive, because
   * automatic silent renew only runs while a portal page is open.
   *
   * @returns true if the session is valid again, false if it cannot be renewed (no refresh token, or the
   *          Keycloak session ended)
   */
  async renewExpiredSession(): Promise<boolean> {
    if (!AuthService.getOidcUserFromLocalStorage()?.refresh_token) {
      return false;
    }

    try {
      const user = await this._oidcClient.signinSilent();
      return !!user?.access_token && !user.expired;
    } catch (error) {
      console.debug('[AuthService]: Session renewal failed.', error);
      return false;
    }
  }

  async signinResourceOwnerCredentials(username: string, password: string): Promise<OidcUser | null> {
    const oidcUser = await this._oidcClient.signinResourceOwnerCredentials({
      username,
      password,
    });

    // If user object exists and access_token is missing, it means otp is enabled
    if (oidcUser && !oidcUser.access_token) {
      await this._oidcClient.removeUser();
      await this._oidcClient.clearStaleState();
      return null;
    }

    return oidcUser;
  }

  signinWithOtp(username: string, otp: string): Promise<OidcUser> {
    return this._oidcClient.signinResourceOwnerCredentials({
      username,
      password: `OTP:${otp}`,
    });
  }

  async createSessionFromToken({ access_token, id_token, refresh_token, session_state }: TokenResponse): Promise<void> {
    const decodedToken = jwtDecode<JwtPayloadType>(access_token);

    const user = new OidcUser({
      access_token,
      id_token,
      refresh_token,
      token_type: 'Bearer',
      profile: {
        sub: decodedToken.sub,
        name: decodedToken.name ?? decodedToken.preferred_username,
        preferred_username: decodedToken.preferred_username,
        email: decodedToken.email,
        given_name: decodedToken.given_name,
        family_name: decodedToken.family_name,
        iss: decodedToken.iss,
        aud: decodedToken.aud,
        exp: decodedToken.exp,
        iat: decodedToken.iat,
      },
      expires_at: decodedToken.exp,
      session_state,
      scope: decodedToken.scope || 'openid profile email',
    });

    return this._oidcClient.storeUser(user);
  }

  /**
   * @param returnTo portal path to open after login; carried through the OIDC round trip as the user state
   *                 and read back with {@link getReturnTo}
   */
  async signinRedirect(identityProvider?: string, returnTo?: string): Promise<void> {
    const extraQueryParams = identityProvider ? { kc_idp_hint: identityProvider } : undefined;
    const state: SigninReturnState | undefined = returnTo ? { returnTo } : undefined;
    return this._oidcClient.signinRedirect({ extraQueryParams, state });
  }

  /** The portal path passed to {@link signinRedirect}, if any. */
  static getReturnTo(user: OidcUser): string | undefined {
    const state = user.state as Partial<SigninReturnState> | undefined;
    return typeof state?.returnTo === 'string' ? state.returnTo : undefined;
  }

  async signinCallback(): Promise<OidcUser> {
    const user = await this._oidcClient.signinCallback();
    if (!user) {
      throw new Error('OIDC signin callback failed - no user returned');
    }
    return user;
  }

  async signout(): Promise<void> {
    clearGupVisit();
    const id_token_hint = AuthService.getOidcUserFromLocalStorage()?.id_token;
    await this._oidcClient.removeUser();
    await this._oidcClient.clearStaleState();
    AuthService.clearLocalStorage();

    return this._oidcClient.signoutRedirect({
      id_token_hint,
    });
  }

  /**
   * Ends the portal and Keycloak sessions for a new Gov.om entry. Unlike {@link signout} it keeps the stored GUP
   * landing, so the user signs in again (THEQA) and continues with that request. Without the Keycloak logout,
   * the THEQA hint would be ignored and Keycloak would sign the previous user back in.
   */
  async signoutForGupEntry(): Promise<void> {
    const id_token_hint = AuthService.getOidcUserFromLocalStorage()?.id_token;
    await this._oidcClient.removeUser();
    await this._oidcClient.clearStaleState();
    AuthService.clearLocalStorage();

    return this._oidcClient.signoutRedirect({
      id_token_hint,
    });
  }

  static clearLocalStorage(): void {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('oidc.')) {
        localStorage.removeItem(key);
      }
    }
  }
}
